import apiClient from './api';
import {
  SystemStatus,
  ChangelogResponse,
  CreateFeatureRequest,
  FeatureRequest,
  SystemService,
  SystemIncident,
  AdminFeatureRequest,
  AdminChangelog,
  UpdateServiceData,
  CreateIncidentData,
  UpdateIncidentData,
  UpdateFeatureRequestData,
  CreateChangelogData,
  UpdateChangelogData,
} from '@/types/status';

/**
 * Status Service
 *
 * Handles API calls for system status, feature requests, and changelog
 *
 * Backend endpoints:
 * - GET /api/status - Public system status (no auth)
 * - GET /api/status/changelog - Public changelog (no auth)
 * - POST /api/status/feature-request - Submit feature request (auth required)
 * - GET /api/status/feature-requests - User's own requests (auth required)
 */

// Backend wraps responses in this structure
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

export const statusService = {
  /**
   * Get current system status (public endpoint)
   */
  async getSystemStatus(): Promise<SystemStatus> {
    const response = await apiClient.get<ApiResponse<SystemStatus>>('/api/status');
    return response.data.data;
  },

  /**
   * Get changelog entries (public endpoint)
   */
  async getChangelog(limit: number = 10): Promise<ChangelogResponse> {
    const response = await apiClient.get<ApiResponse<ChangelogResponse>>('/api/status/changelog', {
      params: { limit },
    });
    return response.data.data;
  },

  /**
   * Submit a feature request (requires auth)
   */
  async submitFeatureRequest(data: CreateFeatureRequest): Promise<FeatureRequest> {
    // If there's a screenshot, use FormData
    if (data.screenshot) {
      const formData = new FormData();
      formData.append('type', data.type);
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('screenshot', data.screenshot);

      const response = await apiClient.post<ApiResponse<FeatureRequest>>(
        '/api/status/feature-request',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.data;
    } else {
      // No screenshot, use JSON
      const response = await apiClient.post<ApiResponse<FeatureRequest>>(
        '/api/status/feature-request',
        {
          type: data.type,
          title: data.title,
          description: data.description,
        }
      );
      return response.data.data;
    }
  },

  /**
   * Get user's own feature requests (requires auth)
   */
  async getUserFeatureRequests(): Promise<FeatureRequest[]> {
    const response = await apiClient.get<ApiResponse<FeatureRequest[]>>(
      '/api/status/feature-requests'
    );
    return response.data.data;
  },

  // ============================================================================
  // ADMIN ENDPOINTS (require isSuperAdmin: true)
  // ============================================================================

  /**
   * Get all services (admin only)
   */
  async getAdminServices(): Promise<SystemService[]> {
    const response = await apiClient.get<ApiResponse<SystemService[]>>('/api/status/admin/services');
    return response.data.data;
  },

  /**
   * Update service status (admin only)
   */
  async updateService(
    slug: string,
    data: UpdateServiceData
  ): Promise<SystemService> {
    const response = await apiClient.patch<ApiResponse<SystemService>>(
      `/api/status/admin/services/${slug}`,
      data
    );
    return response.data.data;
  },

  /**
   * Get all incidents (admin only)
   */
  async getAdminIncidents(): Promise<SystemIncident[]> {
    const response = await apiClient.get<ApiResponse<SystemIncident[]>>('/api/status/admin/incidents');
    return response.data.data;
  },

  /**
   * Create incident (admin only)
   */
  async createIncident(data: CreateIncidentData): Promise<SystemIncident> {
    const response = await apiClient.post<ApiResponse<SystemIncident>>('/api/status/admin/incidents', data);
    return response.data.data;
  },

  /**
   * Update incident (admin only)
   */
  async updateIncident(id: number, data: UpdateIncidentData): Promise<SystemIncident> {
    const response = await apiClient.patch<ApiResponse<SystemIncident>>(
      `/api/status/admin/incidents/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Add incident update (admin only)
   */
  async addIncidentUpdate(id: number, message: string): Promise<SystemIncident> {
    const response = await apiClient.post<ApiResponse<SystemIncident>>(
      `/api/status/admin/incidents/${id}/updates`,
      { message }
    );
    return response.data.data;
  },

  /**
   * Resolve incident (admin only)
   */
  async resolveIncident(id: number, message: string): Promise<SystemIncident> {
    const response = await apiClient.post<ApiResponse<SystemIncident>>(
      `/api/status/admin/incidents/${id}/resolve`,
      { message }
    );
    return response.data.data;
  },

  /**
   * Get all feature requests (admin only)
   */
  async getAdminFeatureRequests(): Promise<AdminFeatureRequest[]> {
    const response = await apiClient.get<ApiResponse<AdminFeatureRequest[]>>(
      '/api/status/admin/feature-requests'
    );
    return response.data.data;
  },

  /**
   * Update feature request (admin only)
   */
  async updateFeatureRequest(
    id: number,
    data: UpdateFeatureRequestData
  ): Promise<AdminFeatureRequest> {
    const response = await apiClient.patch<ApiResponse<AdminFeatureRequest>>(
      `/api/status/admin/feature-requests/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Get all changelog entries (admin only)
   */
  async getAdminChangelog(): Promise<AdminChangelog[]> {
    const response = await apiClient.get<ApiResponse<AdminChangelog[]>>('/api/status/admin/changelog');
    return response.data.data;
  },

  /**
   * Create changelog entry (admin only)
   */
  async createChangelogEntry(data: CreateChangelogData): Promise<AdminChangelog> {
    const response = await apiClient.post<ApiResponse<AdminChangelog>>('/api/status/admin/changelog', data);
    return response.data.data;
  },

  /**
   * Update changelog entry (admin only)
   */
  async updateChangelogEntry(id: number, data: UpdateChangelogData): Promise<AdminChangelog> {
    const response = await apiClient.patch<ApiResponse<AdminChangelog>>(
      `/api/status/admin/changelog/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete changelog entry (admin only)
   */
  async deleteChangelogEntry(id: number): Promise<void> {
    await apiClient.delete(`/api/status/admin/changelog/${id}`);
  },
};
